import React, { useEffect, useState } from 'react';
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  addDoc,
  Timestamp,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject, listAll } from 'firebase/storage';
import { db, storage } from '../firebase';

export default function CarEditor() {
  const [cars, setCars] = useState([]);
  const [selectedCar, setSelectedCar] = useState(null);
  const [notes, setNotes] = useState('');
  const [newImage, setNewImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [carDetails, setCarDetails] = useState({
    make: '',
    model: '',
    rego: '',
    color: '',
    year: '',
  });
  const [carNotes, setCarNotes] = useState([]);

  useEffect(() => {
    fetchCars();
  }, []);

  useEffect(() => {
    if (selectedCar) {
      setCarDetails({
        make: selectedCar.make || '',
        model: selectedCar.model || '',
        rego: selectedCar.rego || '',
        color: selectedCar.color || '',
        year: selectedCar.year || '',
      });
      fetchCarNotes(selectedCar.id);
    }
  }, [selectedCar]);

  const fetchCars = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'cars'));
      const carList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setCars(carList);
    } catch (error) {
      console.error('Error fetching cars:', error);
    }
  };

  const fetchCarNotes = async (carId) => {
    try {
      const notesRef = collection(db, `cars/${carId}/notes`);
      const notesSnapshot = await getDocs(notesRef);
      const notesList = notesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setCarNotes(notesList);
    } catch (error) {
      console.error('Error fetching car notes:', error);
    }
  };

  const handleUpdate = async () => {
    if (!selectedCar) return;
    
    setUploading(true);
    try {
      const updates = { notes };

      if (newImage) {
        const imageRef = ref(storage, `carImages/${selectedCar.id}/${Date.now()}-${newImage.name}`);
        await uploadBytes(imageRef, newImage);
        const downloadURL = await getDownloadURL(imageRef);
        updates.imageUrls = [...(selectedCar.imageUrls || []), downloadURL];
      }

      await updateDoc(doc(db, 'cars', selectedCar.id), updates);
      
      // Refresh cars list
      await fetchCars();
      
      alert('✅ Car info updated successfully!');
      setNewImage(null);
      setNotes('');
    } catch (error) {
      console.error('Error updating car:', error);
      alert('❌ Failed to update car info.');
    } finally {
      setUploading(false);
    }
  };

  const handleEditCar = async () => {
    if (!selectedCar) return;
    
    setUploading(true);
    try {
      await updateDoc(doc(db, 'cars', selectedCar.id), carDetails);
      
      // Refresh cars list
      await fetchCars();
      
      alert('✅ Car details updated successfully!');
      setEditing(false);
    } catch (error) {
      console.error('Error updating car details:', error);
      alert('❌ Failed to update car details.');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteImage = async (imageUrl, imageIndex) => {
    if (!selectedCar) return;
    
    if (selectedCar.imageUrls.length <= 1) {
      alert('❌ Cannot delete the last image. Car must have at least one image.');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this image?')) {
      return;
    }

    try {
      // Remove image from Firestore
      const updatedImageUrls = selectedCar.imageUrls.filter((_, index) => index !== imageIndex);
      await updateDoc(doc(db, 'cars', selectedCar.id), {
        imageUrls: updatedImageUrls
      });

      // Try to delete from Storage (optional - may not exist)
      try {
        const imageRef = ref(storage, imageUrl);
        await deleteObject(imageRef);
      } catch (storageError) {
        console.log('Image not found in storage, continuing...');
      }

      // Update local state immediately
      setSelectedCar({
        ...selectedCar,
        imageUrls: updatedImageUrls
      });
      
      // Also refresh cars list
      await fetchCars();
      
      alert('✅ Image deleted successfully!');
    } catch (error) {
      console.error('Error deleting image:', error);
      alert('❌ Failed to delete image.');
    }
  };

  const handleAddNote = async () => {
    if (!selectedCar || !notes.trim()) return;

    try {
      const noteRef = await addDoc(collection(db, `cars/${selectedCar.id}/notes`), {
        text: notes.trim(),
        createdAt: Timestamp.now(),
      });

      setCarNotes([...carNotes, { id: noteRef.id, text: notes.trim(), createdAt: Timestamp.now() }]);
      setNotes('');
      alert('✅ Note added successfully!');
    } catch (error) {
      console.error('Error adding note:', error);
      alert('❌ Failed to add note.');
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!selectedCar) return;

    if (!window.confirm('Are you sure you want to delete this note?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, `cars/${selectedCar.id}/notes`, noteId));
      setCarNotes(carNotes.filter(note => note.id !== noteId));
      alert('✅ Note deleted successfully!');
    } catch (error) {
      console.error('Error deleting note:', error);
      alert('❌ Failed to delete note.');
    }
  };

  const deleteCarImages = async (carId) => {
    try {
      const carImagesRef = ref(storage, `carImages/${carId}`);
      const imagesList = await listAll(carImagesRef);
      
      // Delete all images in the car's folder
      const deletePromises = imagesList.items.map(imageRef => deleteObject(imageRef));
      await Promise.all(deletePromises);
    } catch (error) {
      console.error('Error deleting car images:', error);
    }
  };

  const handleDelete = async (carId) => {
    if (!window.confirm('Are you sure you want to delete this car? This action cannot be undone.')) {
      return;
    }

    setDeleting(true);
    try {
      // Delete car images from Storage
      await deleteCarImages(carId);
      
      // Delete the car document from Firestore
      await deleteDoc(doc(db, 'cars', carId));
      
      // Update local state
      setCars(prev => prev.filter(car => car.id !== carId));
      setSelectedCar(null);
      setNotes('');
      setNewImage(null);
      setCarNotes([]);
      
      alert('✅ Car deleted successfully!');
    } catch (error) {
      console.error('Error deleting car:', error);
      alert('❌ Failed to delete car. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div style={container}>
      <div style={selectContainer}>
        <label style={label}>Select a Car to Edit</label>
        <select
          style={select}
          onChange={(e) => {
            const car = cars.find(c => c.id === e.target.value);
            setSelectedCar(car);
            setNotes(car?.notes || '');
            setEditing(false);
          }}
          value={selectedCar?.id || ''}
        >
          <option value="">-- Select a car --</option>
          {cars.map(car => (
            <option key={car.id} value={car.id}>
              {car.make} {car.model} ({car.rego}) - {car.status}
            </option>
          ))}
        </select>
      </div>

      {selectedCar && (
        <div style={container}>
          <div style={carHeader}>
            <h4 style={carTitle}>{selectedCar.make} {selectedCar.model}</h4>
            <span style={statusBadge(selectedCar.status)}>{selectedCar.status}</span>
          </div>

          {/* Car Details Section */}
          <div style={section}>
            <div style={sectionHeader}>
              <h5 style={sectionTitle}>Car Details</h5>
              <button 
                onClick={() => setEditing(!editing)} 
                style={editButton}
              >
                {editing ? 'Cancel' : 'Edit Details'}
              </button>
            </div>

            {editing ? (
              <div style={editForm}>
                <div style={formRow}>
                  <div style={formField}>
                    <label style={label}>Make</label>
                    <input
                      type="text"
                      value={carDetails.make}
                      onChange={(e) => setCarDetails({...carDetails, make: e.target.value})}
                      style={input}
                    />
                  </div>
                  <div style={formField}>
                    <label style={label}>Model</label>
                    <input
                      type="text"
                      value={carDetails.model}
                      onChange={(e) => setCarDetails({...carDetails, model: e.target.value})}
                      style={input}
                    />
                  </div>
                </div>
                <div style={formRow}>
                  <div style={formField}>
                    <label style={label}>Registration</label>
                    <input
                      type="text"
                      value={carDetails.rego}
                      onChange={(e) => setCarDetails({...carDetails, rego: e.target.value})}
                      style={input}
                    />
                  </div>
                  <div style={formField}>
                    <label style={label}>Color</label>
                    <input
                      type="text"
                      value={carDetails.color}
                      onChange={(e) => setCarDetails({...carDetails, color: e.target.value})}
                      style={input}
                    />
                  </div>
                </div>
                <div style={formRow}>
                  <div style={formField}>
                    <label style={label}>Year</label>
                    <input
                      type="number"
                      value={carDetails.year}
                      onChange={(e) => setCarDetails({...carDetails, year: e.target.value})}
                      style={input}
                      min="1900"
                      max={new Date().getFullYear()}
                    />
                  </div>
                </div>
                <button 
                  onClick={handleEditCar} 
                  style={saveButton}
                  disabled={uploading}
                >
                  {uploading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            ) : (
              <div style={infoGrid}>
                <div style={infoItem}>
                  <strong>Registration:</strong> {selectedCar.rego}
                </div>
                <div style={infoItem}>
                  <strong>Color:</strong> {selectedCar.color || 'N/A'}
                </div>
                <div style={infoItem}>
                  <strong>Year:</strong> {selectedCar.year || 'N/A'}
                </div>
              </div>
            )}
          </div>

          {/* Images Section */}
          {(selectedCar.imageUrls || []).length > 0 && (
            <div style={section}>
              <h5 style={sectionTitle}>Car Images</h5>
              <div style={imagesGrid}>
                {selectedCar.imageUrls.map((url, index) => (
                  <div key={index} style={imageContainer}>
                    <img 
                      src={url} 
                      alt={`Car ${index + 1}`} 
                      style={carImage} 
                    />
                    <button
                      onClick={() => handleDeleteImage(url, index)}
                      style={deleteImageButton}
                      disabled={selectedCar.imageUrls.length <= 1}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Notes Section */}
          <div style={section}>
            <h5 style={sectionTitle}>Notes</h5>
            
            {/* Existing Notes */}
            {carNotes.length > 0 && (
              <div style={notesList}>
                {carNotes.map((note) => (
                  <div key={note.id} style={noteItem}>
                    <p style={noteText}>{note.text}</p>
                    {note.imageUrl && (
                      <div style={noteImageContainer}>
                        <img 
                          src={note.imageUrl} 
                          alt="Note attachment" 
                          style={noteImage} 
                        />
                      </div>
                    )}
                    <div style={noteMeta}>
                      <span style={noteDate}>
                        {note.createdAt?.toDate?.()?.toLocaleDateString() || 'Unknown date'}
                      </span>
                      <button
                        onClick={() => handleDeleteNote(note.id)}
                        style={deleteNoteButton}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Add New Note */}
            <div style={addNoteSection}>
              <textarea 
                style={textarea} 
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add a new note about this car..."
                rows={3}
              />
              <button 
                onClick={handleAddNote} 
                style={addNoteButton}
                disabled={!notes.trim()}
              >
                Add Note
              </button>
            </div>
          </div>

          {/* Add New Image */}
          <div style={section}>
            <h5 style={sectionTitle}>Add New Image</h5>
            <input 
              type="file" 
              accept="image/*"
              onChange={(e) => setNewImage(e.target.files[0])}
              style={fileInput}
            />
            <button 
              onClick={handleUpdate} 
              style={updateButton}
              disabled={uploading || !newImage}
            >
              {uploading ? 'Uploading...' : 'Upload Image'}
            </button>
          </div>

          {/* Action Buttons */}
          <div style={buttonGroup}>
            <button
              onClick={() => handleDelete(selectedCar.id)}
              style={deleteButton}
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Delete Car'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Styles
const container = {
  maxWidth: '800px',
};

const selectContainer = {
  marginBottom: '24px',
};

const label = {
  display: 'block',
  marginBottom: '8px',
  fontSize: '14px',
  fontWeight: 500,
  color: '#374151',
};

const select = {
  width: '100%',
  padding: '12px 14px',
  borderRadius: '8px',
  border: '1px solid #d1d5db',
  fontSize: '14px',
  backgroundColor: '#f9fafb',
  outline: 'none',
};

const carHeader = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '20px',
};

const carTitle = {
  fontSize: '18px',
  fontWeight: 600,
  color: '#111827',
  margin: 0,
};

const statusBadge = (status) => ({
  padding: '4px 12px',
  borderRadius: '20px',
  fontSize: '12px',
  fontWeight: 600,
  backgroundColor: status === 'available' ? '#dcfce7' : '#fef2f2',
  color: status === 'available' ? '#166534' : '#dc2626',
});

const section = {
  marginBottom: '24px',
  padding: '16px',
  backgroundColor: '#fff',
  borderRadius: '8px',
  border: '1px solid #e5e7eb',
};

const sectionHeader = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '16px',
};

const sectionTitle = {
  fontSize: '16px',
  fontWeight: 600,
  color: '#374151',
  margin: 0,
};

const editButton = {
  padding: '6px 12px',
  backgroundColor: '#3b82f6',
  color: '#fff',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  fontSize: '12px',
  fontWeight: 500,
};

const editForm = {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
};

const formRow = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '16px',
};

const formField = {
  display: 'flex',
  flexDirection: 'column',
};

const input = {
  padding: '8px 12px',
  borderRadius: '6px',
  border: '1px solid #d1d5db',
  fontSize: '14px',
  backgroundColor: '#fff',
  outline: 'none',
};

const saveButton = {
  padding: '8px 16px',
  backgroundColor: '#10b981',
  color: '#fff',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: 500,
  alignSelf: 'flex-start',
};

const infoGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: '12px',
};

const infoItem = {
  fontSize: '14px',
  color: '#6b7280',
};

const imagesGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
  gap: '16px',
};

const imageContainer = {
  position: 'relative',
  display: 'inline-block',
};

const carImage = {
  width: '100%',
  height: '100px',
  objectFit: 'cover',
  borderRadius: '8px',
  border: '1px solid #e5e7eb',
};

const deleteImageButton = {
  position: 'absolute',
  top: '-8px',
  right: '-8px',
  width: '24px',
  height: '24px',
  borderRadius: '50%',
  backgroundColor: '#ef4444',
  color: '#fff',
  border: 'none',
  cursor: 'pointer',
  fontSize: '16px',
  fontWeight: 'bold',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const notesList = {
  marginBottom: '16px',
};

const noteItem = {
  backgroundColor: '#f9fafb',
  padding: '12px',
  borderRadius: '6px',
  marginBottom: '8px',
  border: '1px solid #e5e7eb',
};

const noteText = {
  margin: '0 0 8px 0',
  fontSize: '14px',
  color: '#374151',
};

const noteMeta = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const noteDate = {
  fontSize: '12px',
  color: '#6b7280',
};

const deleteNoteButton = {
  padding: '4px 8px',
  backgroundColor: '#ef4444',
  color: '#fff',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '12px',
};

const addNoteSection = {
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
};

const textarea = {
  width: '100%',
  padding: '12px 14px',
  borderRadius: '8px',
  border: '1px solid #d1d5db',
  fontSize: '14px',
  backgroundColor: '#fff',
  outline: 'none',
  resize: 'vertical',
  fontFamily: 'inherit',
};

const addNoteButton = {
  padding: '8px 16px',
  backgroundColor: '#3b82f6',
  color: '#fff',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: 500,
  alignSelf: 'flex-start',
};

const fileInput = {
  width: '100%',
  padding: '8px',
  borderRadius: '8px',
  border: '1px solid #d1d5db',
  fontSize: '14px',
  marginBottom: '12px',
};

const updateButton = {
  padding: '8px 16px',
  backgroundColor: '#3b82f6',
  color: '#fff',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: 500,
};

const buttonGroup = {
  display: 'flex',
  gap: '12px',
  marginTop: '20px',
};

const deleteButton = {
  flex: 1,
  padding: '12px 16px',
  backgroundColor: '#ef4444',
  color: '#fff',
  border: 'none',
  borderRadius: '8px',
  fontWeight: 600,
  cursor: 'pointer',
  fontSize: '14px',
};

const noteImageContainer = {
  marginTop: '8px',
  marginBottom: '8px',
};

const noteImage = {
  width: '100%',
  height: '100px',
  objectFit: 'cover',
  borderRadius: '8px',
  border: '1px solid #e5e7eb',
};
